import DOMPurify from 'dompurify';

/**
 * Sanitizes an HTML string using DOMPurify.
 * Allows safe formatting tags (bold, italic, links, lists, tables, etc.)
 * but strips all scripts, event handlers, and dangerous attributes.
 *
 * Use this wherever you pass untrusted content to dangerouslySetInnerHTML.
 *
 * @param {string} dirty - Raw/untrusted HTML string.
 * @returns {string} - Sanitized HTML string safe for rendering.
 */
export function sanitizeHtml(dirty) {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    // Allow a safe subset of HTML elements
    ALLOWED_TAGS: [
      'a',
      'b',
      'br',
      'blockquote',
      'caption',
      'cite',
      'code',
      'col',
      'colgroup',
      'dd',
      'del',
      'details',
      'dfn',
      'div',
      'dl',
      'dt',
      'em',
      'figcaption',
      'figure',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hr',
      'i',
      'img',
      'ins',
      'kbd',
      'li',
      'mark',
      'ol',
      'p',
      'pre',
      'q',
      's',
      'samp',
      'small',
      'span',
      'strong',
      'sub',
      'summary',
      'sup',
      'table',
      'tbody',
      'td',
      'tfoot',
      'th',
      'thead',
      'time',
      'tr',
      'u',
      'ul',
      'var',
    ],
    // Allow safe attributes only
    ALLOWED_ATTR: [
      'href',
      'src',
      'alt',
      'title',
      'class',
      'id',
      'style',
      'target',
      'rel',
      'width',
      'height',
      'align',
      'valign',
      'colspan',
      'rowspan',
      'datetime',
      'cite',
      'open',
    ],
    // Force all links to open in a new tab safely
    ADD_ATTR: ['target'],
    // Forbid javascript: and data: URIs in href/src
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    // Keep href/src but sanitize the values
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Strips all HTML tags from a string, returning plain text only.
 * Use this for fields that should never contain HTML (e.g. task notes,
 * plain-text metadata fields going to external APIs).
 *
 * @param {string} dirty - Raw string that may contain HTML tags.
 * @returns {string} - Plain text with all tags removed.
 */
export function stripHtml(dirty) {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}
